/**
 * Rechterpaneel: eigenschappen van wat je op dit moment onder handen hebt.
 *
 * Bewust contextueel en klein: is er een blok geselecteerd, dan gaat het over
 * dat blok; anders over de pagina. Er staat nooit een lijst met alle
 * mogelijke instellingen tegelijk -- dat is precies wat een bouwer
 * overweldigend maakt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlignCenter, AlignLeft, AlignRight, ArrowDown, ArrowUp, Copy, MousePointerSquareDashed, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { blockDefinition } from '../blocks/registry';
import { inferLayout } from '../layouts';
import { placementFor, setPlacement, GRID_COLUMNS } from '../grid';
import { displayPath, normalizeSlug } from '../slug';
import { COLUMN_COUNTS } from '../types';
import type { ColorRole, ColumnLayout, ContentBlock, ElementIconPosition, EntranceEffect, MediaTarget, SiteChrome, SitePage, SiteSection, ViewportKey } from '../types';
import { ButtonInspector } from './ButtonInspector';
import { CardInspector } from './CardInspector';
import { DesignPanel } from './DesignPanel';
import { FooterInspector, HeaderInspector } from './ChromeInspector';
import { LayoutChooser } from './LayoutChooser';
import { IconPicker } from './IconPicker';

interface InspectorProps {
  page: SitePage | null;
  block: ContentBlock | null;
  section: SiteSection | null;
  pages: SitePage[];
  onChangeBlockText: (blockId: string, text: string) => void;
  onUpdateBlock: (blockId: string, patch: Partial<ContentBlock>) => void;
  onDuplicateBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onUpdatePage: (
    pageId: string,
    patch: Partial<Pick<SitePage, 'title' | 'slug' | 'description'>>,
  ) => void;
  site: SiteChrome | null;
  onUpdateTheme: (patch: Partial<SiteChrome['theme']>) => void;
  onUpdateHeader: (patch: Partial<SiteChrome['header']>) => void;
  onUpdateSection: (sectionId: string, patch: Partial<SiteSection>) => void;
  onChangeSectionLayout: (sectionId: string, layout: ColumnLayout) => void;
  onMoveSection: (sectionId: string, direction: 'up' | 'down') => void;
  onDuplicateSection: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
  mode: string;
  onModeChange: (mode: 'INSPECTOR_PAGE' | 'SITE_DESIGN') => void;
  onClose: () => void;
  onUpdateFooter: (patch: Partial<SiteChrome['footer']>) => void;
  selectedFooterElementId?: string | null;
  onUpdateSettings: (patch: Partial<SiteChrome['settings']>) => void;
  onOpenMedia: (target: MediaTarget) => void;
  onOpenDeveloper?: () => void;
  viewport: ViewportKey;
}

const fieldClass =
  'mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20';

export function Inspector({
  page,
  block,
  section,
  pages,
  onChangeBlockText,
  onUpdateBlock,
  onDuplicateBlock,
  onDeleteBlock,
  onUpdatePage,
  site,
  onUpdateTheme,
  onUpdateHeader,
  onUpdateSection,
  onChangeSectionLayout,
  onMoveSection,
  onDuplicateSection,
  onDeleteSection,
  mode,
  onModeChange,
  onClose,
  onUpdateFooter,
  selectedFooterElementId,
  onUpdateSettings,
  onOpenMedia,
  onOpenDeveloper,
  viewport,
}: InspectorProps) {
  const sectionIndex = page?.sections?.findIndex((item) => item.sectionId === section?.sectionId) ?? -1;
  const sectionCount = page?.sections?.length ?? 0;
  return (
    <aside className="flex h-full w-full flex-col border-l border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 pb-3 pt-4">
        <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
          {mode === 'INSPECTOR_HEADER' ? 'Header'
            : mode === 'INSPECTOR_FOOTER' ? 'Footer'
            : mode === 'SITE_DESIGN' ? 'Ontwerp'
            : block ? 'Element' : section ? 'Sectie' : 'Pagina'}
        </h2>
        <button type="button" onClick={onClose} aria-label="Paneel sluiten" title="Paneel sluiten" className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        {mode === 'INSPECTOR_HEADER' && site ? (
          <HeaderInspector site={site} pages={pages} onUpdateHeader={onUpdateHeader} onUpdateSettings={onUpdateSettings} onOpenMedia={onOpenMedia} />
        ) : mode === 'INSPECTOR_FOOTER' && site ? (
          <FooterInspector site={site} pages={pages} onUpdateFooter={onUpdateFooter} selectedElementId={selectedFooterElementId} onOpenMedia={onOpenMedia} />
        ) : mode === 'SITE_DESIGN' && site ? (
          <DesignPanel site={site} onUpdateTheme={onUpdateTheme} onBack={() => onModeChange('INSPECTOR_PAGE')} />
        ) : block ? (
          <div className="space-y-4">
            {page && developerCssAffectsBlock(page, block) ? <DeveloperCssNotice onOpenDeveloper={onOpenDeveloper} /> : null}
            <BlockInspector
              block={block}
              pages={pages}
              currentPageId={page?.pageId ?? null}
              onPatch={(patch) => onUpdateBlock(block.blockId, patch)}
              onChangeText={(text) => onChangeBlockText(block.blockId, text)}
              onDuplicate={() => onDuplicateBlock(block.blockId)}
              onDelete={() => onDeleteBlock(block.blockId)}
              viewport={viewport}
              onOpenMedia={onOpenMedia}
            />
          </div>
        ) : page ? (
          section ? (
            <SectionInspector
              section={section}
              onUpdate={onUpdateSection}
              onChangeLayout={onChangeSectionLayout}
              onMove={onMoveSection}
              onDuplicate={onDuplicateSection}
              onDelete={onDeleteSection}
              canMoveUp={sectionIndex > 0}
              canMoveDown={sectionIndex !== -1 && sectionIndex < sectionCount - 1}
              onOpenMedia={onOpenMedia}
            />
          ) : <div className="space-y-4">
            {page.developerCss ? <DeveloperCssNotice onOpenDeveloper={onOpenDeveloper} /> : null}
            <PageInspector page={page} pages={pages} onUpdatePage={onUpdatePage} site={site} onModeChange={onModeChange} />
          </div>
        ) : (
          <EmptyInspector />
        )}
      </div>
    </aside>
  );
}

function SectionInspector({
  section,
  onUpdate,
  onChangeLayout,
  onMove,
  onDuplicate,
  onDelete,
  canMoveUp,
  canMoveDown,
  onOpenMedia,
}: {
  section: SiteSection;
  onUpdate: (sectionId: string, patch: Partial<SiteSection>) => void;
  onChangeLayout: (sectionId: string, layout: ColumnLayout) => void;
  onMove: (sectionId: string, direction: 'up' | 'down') => void;
  onDuplicate: (sectionId: string) => void;
  onDelete: (sectionId: string) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  const layout = section.columnLayout ?? inferLayout(section.columns.length);
  const nextStyle = (patch: Partial<NonNullable<SiteSection['style']>>) =>
    onUpdate(section.sectionId, { style: { ...section.style, ...patch } });
  const background = section.style?.background ?? {};
  const nextBackground = (patch: Partial<typeof background>) =>
    nextStyle({ background: { ...background, ...patch } });

  // How much content a narrower layout would relocate, so the chooser can say
  // so before the user commits to it rather than after.
  const relocatingFor = (target: ColumnLayout) =>
    section.columns.slice(COLUMN_COUNTS[target]).reduce((total, column) => total + column.blocks.length, 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-slate-900">Sectie</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          Bepaal de indeling, ruimte en stijl van deze band op de pagina.
        </p>
      </div>

      <div className="flex gap-1.5">
        <IconAction label="Omhoog" onClick={() => onMove(section.sectionId, 'up')} disabled={!canMoveUp}>
          <ArrowUp className="h-4 w-4" />
        </IconAction>
        <IconAction label="Omlaag" onClick={() => onMove(section.sectionId, 'down')} disabled={!canMoveDown}>
          <ArrowDown className="h-4 w-4" />
        </IconAction>
        <IconAction label="Dupliceren" onClick={() => onDuplicate(section.sectionId)}>
          <Copy className="h-4 w-4" />
        </IconAction>
        <IconAction label="Verwijderen" destructive onClick={() => onDelete(section.sectionId)}>
          <Trash2 className="h-4 w-4" />
        </IconAction>
      </div>

      <LayoutChooser
        value={layout}
        onChange={(next) => onChangeLayout(section.sectionId, next)}
        relocatingFor={relocatingFor}
      />

      <Choice
        label="Ruimte"
        value={section.style?.spacing ?? 'normal'}
        options={[
          ['compact', 'Compact'],
          ['normal', 'Normaal'],
          ['spacious', 'Ruim'],
          ['extra', 'Extra ruim'],
        ]}
        onChange={(value) => nextStyle({ spacing: value as NonNullable<SiteSection['style']>['spacing'] })}
      />

      <Choice
        label="Bovenruimte"
        value={section.style?.topSpacing ?? 'normal'}
        options={[
          ['flush', 'Aansluitend'],
          ['compact', 'Kleine ruimte'],
          ['normal', 'Normale ruimte'],
        ]}
        onChange={(value) => nextStyle({ topSpacing: value as NonNullable<SiteSection['style']>['topSpacing'] })}
      />
      <p className="-mt-4 text-xs leading-relaxed text-slate-500">
        Kies <strong>Aansluitend</strong> om de eerste sectie direct onder de navigatie te laten beginnen.
      </p>

      <Choice
        label="Inhoud verticaal"
        value={section.style?.verticalAlign ?? 'center'}
        options={[
          ['top', 'Boven'],
          ['center', 'Midden'],
          ['bottom', 'Onder'],
        ]}
        onChange={(value) => nextStyle({ verticalAlign: value as NonNullable<SiteSection['style']>['verticalAlign'] })}
      />
      <p className="-mt-4 text-xs leading-relaxed text-slate-500">
        Dit verplaatst de inhoud binnen de sectie; het verandert de volgorde van secties niet.
      </p>

      <Choice
        label="Breedte"
        value={section.style?.widthMode ?? 'content'}
        options={[
          ['content', 'Normaal'],
          ['wide', 'Breed'],
          ['full', 'Volledige breedte'],
        ]}
        onChange={(value) => nextStyle({ widthMode: value as NonNullable<SiteSection['style']>['widthMode'] })}
      />

      <Choice
        label="Tekstkleur preset"
        value={section.style?.colorScheme ?? 'light'}
        options={[
          ['light', 'Donkere tekst'],
          ['dark', 'Lichte tekst'],
        ]}
        onChange={(value) => nextStyle({ colorScheme: value as 'light' | 'dark', textColor: null })}
      />

      <HexColorField
        label="Exacte tekstkleur"
        value={section.style?.textColor}
        onChange={(value) => nextStyle({ textColor: value })}
      />

      <Choice
        label="Uitlijning"
        value={section.style?.textAlign ?? 'left'}
        options={[['left', 'Links'], ['center', 'Gecentreerd'], ['right', 'Rechts']]}
        onChange={(value) => nextStyle({ textAlign: value as 'left' | 'center' | 'right' })}
      />

      <Choice
        label="Minimale hoogte"
        value={section.style?.minHeight ?? 'auto'}
        options={[['auto', 'Automatisch'], ['medium', 'Middel'], ['tall', 'Hoog'], ['screen', 'Volledig scherm']]}
        onChange={(value) => nextStyle({ minHeight: value as SectionStyleValue['minHeight'], ...(value === 'auto' ? { height: undefined } : {}) })}
      />

      <label className="block text-sm font-semibold text-slate-800">
        Vaste minimumhoogte desktop (px)
        <input
          type="number"
          min={160}
          max={2000}
          value={section.style?.height?.desktop ?? ''}
          placeholder="Automatisch"
          disabled={!section.style?.height}
          onChange={(event) => nextStyle({ height: { ...(section.style?.height ?? {}), desktop: Math.max(160, Math.min(2000, Number(event.target.value) || 560)) } })}
          className={`${fieldClass} mt-1.5`}
        />
        <span className="mt-1 block text-xs font-normal text-slate-500">Automatisch groeit de sectie mee met de inhoud. Sleep de onderrand om een vaste minimumhoogte te kiezen.</span>
      </label>

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Achtergrond</h4>
        <Choice
          label="Type"
          value={background.mode ?? 'none'}
          options={[['none', 'Geen'], ['solid', 'Kleur'], ['gradient', 'Verloop'], ['image', 'Afbeelding']]}
          onChange={(value) => nextBackground({ mode: value as 'none' | 'solid' | 'gradient' | 'image' })}
        />

        {background.mode === 'solid' ? (
          <>
            <Choice
              label="Kleur"
              value={background.color ?? 'surface'}
              options={COLOR_ROLE_OPTIONS}
              onChange={(value) => nextBackground({ color: value as ColorRole, customColor: null, legacyColor: null })}
            />
            <HexColorField
              label="Exacte achtergrondkleur"
              value={background.customColor}
              onChange={(value) => nextBackground({ customColor: value, legacyColor: null })}
            />
          </>
        ) : null}

        {background.mode === 'gradient' ? (
          <>
            <Choice
              label="Startkleur"
              value={background.gradientFrom ?? 'primary'}
              options={COLOR_ROLE_OPTIONS}
              onChange={(value) => nextBackground({ gradientFrom: value as ColorRole, gradientFromCustom: null })}
            />
            <HexColorField label="Exacte startkleur" value={background.gradientFromCustom} onChange={(value) => nextBackground({ gradientFromCustom: value })} />
            <Choice
              label="Eindkleur"
              value={background.gradientTo ?? 'dark'}
              options={COLOR_ROLE_OPTIONS}
              onChange={(value) => nextBackground({ gradientTo: value as ColorRole, gradientToCustom: null })}
            />
            <HexColorField label="Exacte eindkleur" value={background.gradientToCustom} onChange={(value) => nextBackground({ gradientToCustom: value })} />
            <Choice
              label="Richting"
              value={background.gradientDirection ?? 'to-bottom-right'}
              options={[
                ['to-bottom', 'Naar beneden'],
                ['to-right', 'Naar rechts'],
                ['to-bottom-right', 'Diagonaal'],
                ['radial', 'Vanuit het midden'],
              ]}
              onChange={(value) => nextBackground({ gradientDirection: value as GradientDirection })}
            />
          </>
        ) : null}

        {background.mode === 'image' ? (
          <>
            <label className="block">
              <span className="block text-sm font-semibold text-slate-800">Afbeelding</span>
              <button type="button" onClick={() => onOpenMedia({ kind: 'section-background', sectionId: section.sectionId })} className="mt-2 flex w-full items-center justify-between rounded-xl border border-dashed border-teal-300 bg-teal-50 px-3.5 py-3 text-left text-sm font-semibold text-teal-800 hover:bg-teal-100">
                <span>{background.imageAssetId ? 'Afbeelding wijzigen' : 'Afbeelding uploaden'}</span>
                <span aria-hidden="true">→</span>
              </button>
              <input
                value={background.imageAssetId ?? ''}
                onChange={(event) => nextBackground({ imageAssetId: event.target.value || null })}
                placeholder="Of plak een media-id"
                className={fieldClass}
              />
            </label>
            <Choice
              label="Overlaykleur"
              value={background.overlay ?? 'dark'}
              options={COLOR_ROLE_OPTIONS}
              onChange={(value) => nextBackground({ overlay: value as ColorRole, overlayCustom: null })}
            />
            <HexColorField label="Exacte overlaykleur" value={background.overlayCustom} onChange={(value) => nextBackground({ overlayCustom: value })} />
            <label className="block">
              <span className="block text-sm font-semibold text-slate-800">
                Overlaysterkte <span className="font-normal text-slate-400">{background.overlayIntensity ?? 45}%</span>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={background.overlayIntensity ?? 45}
                onChange={(event) => nextBackground({ overlayIntensity: Number(event.target.value) })}
                className="mt-2 w-full"
              />
            </label>
          </>
        ) : null}
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Overgangen</h4>
        <Choice
          label="Bovenrand"
          value={section.style?.topDivider?.style ?? 'none'}
          options={DIVIDER_OPTIONS}
          onChange={(value) => nextStyle({ topDivider: { ...section.style?.topDivider, style: value } })}
        />
        {section.style?.topDivider?.style && section.style.topDivider.style !== 'none' ? (
          <>
            <Choice label="Kleur bovenrand" value={section.style.topDivider.color ?? 'background'} options={COLOR_ROLE_OPTIONS} onChange={(value) => nextStyle({ topDivider: { ...section.style?.topDivider, color: value as ColorRole, customColor: null } })} />
            <HexColorField label="Exacte kleur bovenrand" value={section.style.topDivider.customColor} onChange={(value) => nextStyle({ topDivider: { ...section.style?.topDivider, customColor: value } })} />
            <Choice label="Hoogte bovenrand" value={section.style.topDivider.size ?? 'md'} options={[['sm', 'Klein'], ['md', 'Normaal'], ['lg', 'Groot']]} onChange={(value) => nextStyle({ topDivider: { ...section.style?.topDivider, size: value as 'sm' | 'md' | 'lg' } })} />
            <Choice label="Richting bovenrand" value={section.style.topDivider.flip ? 'flipped' : 'normal'} options={[['normal', 'Normaal'], ['flipped', 'Spiegelen']]} onChange={(value) => nextStyle({ topDivider: { ...section.style?.topDivider, flip: value === 'flipped' } })} />
          </>
        ) : null}
        <Choice
          label="Onderrand"
          value={section.style?.bottomDivider?.style ?? 'none'}
          options={DIVIDER_OPTIONS}
          onChange={(value) => nextStyle({ bottomDivider: { ...section.style?.bottomDivider, style: value } })}
        />
        {section.style?.bottomDivider?.style && section.style.bottomDivider.style !== 'none' ? (
          <>
            <Choice label="Kleur onderrand" value={section.style.bottomDivider.color ?? 'background'} options={COLOR_ROLE_OPTIONS} onChange={(value) => nextStyle({ bottomDivider: { ...section.style?.bottomDivider, color: value as ColorRole, customColor: null } })} />
            <HexColorField label="Exacte kleur onderrand" value={section.style.bottomDivider.customColor} onChange={(value) => nextStyle({ bottomDivider: { ...section.style?.bottomDivider, customColor: value } })} />
            <Choice label="Richting onderrand" value={section.style.bottomDivider.flip ? 'flipped' : 'normal'} options={[['normal', 'Normaal'], ['flipped', 'Spiegelen']]} onChange={(value) => nextStyle({ bottomDivider: { ...section.style?.bottomDivider, flip: value === 'flipped' } })} />
          </>
        ) : null}
        {section.style?.bottomDivider?.style && section.style.bottomDivider.style !== 'none' ? (
          <Choice
            label="Hoogte onderrand"
            value={section.style.bottomDivider.size ?? 'md'}
            options={[['sm', 'Klein'], ['md', 'Normaal'], ['lg', 'Groot']]}
            onChange={(value) => nextStyle({ bottomDivider: { ...section.style?.bottomDivider, size: value as 'sm' | 'md' | 'lg' } })}
          />
        ) : null}
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Decoratie</h4>
        <Choice
          label="Stijl"
          value={section.style?.decoration?.style ?? 'none'}
          options={[
            ['none', 'Geen'], ['glow', 'Gloed'], ['lines', 'Lijnen'],
            ['grid', 'Raster'], ['dots', 'Stippen'], ['blobs', 'Vlekken'],
          ]}
          onChange={(value) => nextStyle({ decoration: { ...section.style?.decoration, style: value } })}
        />
        {section.style?.decoration?.style && section.style.decoration.style !== 'none' ? (
          <>
            <Choice
              label="Sterkte"
              value={section.style.decoration.intensity ?? 'subtle'}
              options={[['subtle', 'Subtiel'], ['medium', 'Medium'], ['strong', 'Sterk']]}
              onChange={(value) => nextStyle({ decoration: { ...section.style?.decoration, intensity: value as 'subtle' | 'medium' | 'strong' } })}
            />
            <Choice
              label="Positie"
              value={section.style.decoration.position ?? 'center'}
              options={[
                ['center', 'Midden'], ['top-left', 'Linksboven'], ['top-right', 'Rechtsboven'],
                ['bottom-left', 'Linksonder'], ['bottom-right', 'Rechtsonder'],
              ]}
              onChange={(value) => nextStyle({ decoration: { ...section.style?.decoration, position: value } })}
            />
          <Choice
            label="Kleur"
            value={section.style.decoration.color ?? 'primary'}
            options={COLOR_ROLE_OPTIONS}
              onChange={(value) => nextStyle({ decoration: { ...section.style?.decoration, color: value as ColorRole, customColor: null } })}
            />
            <HexColorField label="Exacte decoratiekleur" value={section.style.decoration.customColor} onChange={(value) => nextStyle({ decoration: { ...section.style?.decoration, customColor: value } })} />
          </>
        ) : null}
      </div>

      <Choice
        label="Beweging bij tonen"
        value={section.style?.motion ?? 'none'}
        options={[['none', 'Geen'], ['fade', 'Vervagen'], ['fade-up', 'Omhoog'], ['scale', 'Vergroten']]}
        onChange={(value) => nextStyle({ motion: value as SectionStyleValue['motion'] })}
      />

      <label className="block">
        <span className="block text-sm font-semibold text-slate-800">
          Anker <span className="font-normal text-slate-400">(voor knoppen die hierheen springen)</span>
        </span>
        <input
          value={section.anchor ?? ''}
          onChange={(event) => onUpdate(section.sectionId, { anchor: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') || undefined })}
          placeholder="bijv. diensten"
          className={fieldClass}
        />
      </label>
    </div>
  );
}

type SectionStyleValue = NonNullable<SiteSection['style']>;
type GradientDirection = NonNullable<NonNullable<SectionStyleValue['background']>['gradientDirection']>;

const COLOR_ROLE_OPTIONS: Array<[string, string]> = [
  ['primary', 'Hoofdkleur'], ['secondary', 'Tweede'], ['accent', 'Accent'],
  ['background', 'Pagina'], ['surface', 'Vlak'], ['text', 'Tekst'], ['muted', 'Gedempt'], ['dark', 'Donker'], ['light', 'Wit'],
];

const DIVIDER_OPTIONS: Array<[string, string]> = [
  ['none', 'Geen'], ['wave', 'Golf'], ['curve', 'Boog'],
  ['diagonal', 'Schuin'], ['triangle', 'Driehoek'], ['tilt', 'Helling'],
];

/** A compact segmented control. Used instead of a `<select>` wherever the
 * option set is small enough to show at once -- one click instead of two, and
 * the available choices are visible without opening anything. */
function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div role="radiogroup" aria-label={label} className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map(([optionValue, optionLabel]) => (
          <button
            key={optionValue}
            type="button"
            role="radio"
            aria-checked={value === optionValue}
            onClick={() => onChange(optionValue)}
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
              value === optionValue
                ? 'border-teal-400 bg-teal-50 text-teal-800'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            {optionLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex flex-1 items-center justify-center rounded-lg border border-slate-200 py-2 text-slate-600 transition',
        disabled
          ? 'cursor-not-allowed opacity-40'
          : destructive
            ? 'hover:border-red-200 hover:bg-red-50 hover:text-red-700'
            : 'hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700',
      )}
    >
      {children}
    </button>
  );
}

function EmptyInspector() {
  return (
    <div className="pt-6 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
        <MousePointerSquareDashed className="h-5 w-5" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-700">Niets geselecteerd</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Klik links op een pagina, of op een blok in de pagina.
      </p>
    </div>
  );
}

function BlockInspector({
  block,
  pages,
  currentPageId,
  onPatch,
  onChangeText,
  onDuplicate,
  onDelete,
  viewport,
  onOpenMedia,
}: {
  block: ContentBlock;
  pages: SitePage[];
  currentPageId: string | null;
  onPatch: (patch: Partial<ContentBlock>) => void;
  onChangeText: (text: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  viewport: ViewportKey;
  onOpenMedia: (target: MediaTarget) => void;
}) {
  const definition = blockDefinition(block.type);
  const Icon = definition.icon;
  const placement = placementFor(block.layout, viewport);
  const inherited = viewport !== 'desktop' && !block.layout?.[viewport];
  const typography = block.typography ?? {};
  const typographyInherited = Object.keys(typography).length === 0;
  const resetTypography = () => onPatch({ typography: undefined });
  const patchPlacement = (patch: Partial<typeof placement>) => onPatch({ layout: setPlacement(block.layout, viewport, patch) });
  const resetPlacement = () => {
    if (viewport === 'desktop') return;
    const layout = { desktop: block.layout?.desktop ?? placementFor(undefined, 'desktop'), tablet: block.layout?.tablet, mobile: block.layout?.mobile };
    delete layout[viewport];
    onPatch({ layout });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-3.5 py-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-600 shadow-sm">
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-bold text-slate-900">{definition.label}</span>
          <span className="block text-xs text-slate-500">{definition.description}</span>
        </span>
      </div>

      <div className="flex gap-2" aria-label="Elementacties">
        <IconAction label="Dupliceren" onClick={onDuplicate}>
          <Copy className="h-4 w-4" />
        </IconAction>
        <IconAction label="Verwijderen" destructive onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </IconAction>
      </div>

      {block.type === 'button' ? (
        <ButtonInspector block={block} pages={pages} currentPageId={currentPageId} onPatch={onPatch} />
      ) : block.type === 'cards' ? (
        <CardInspector block={block} onPatch={onPatch} />
      ) : (
        <TypedBlockContent block={block} definition={definition} onChangeText={onChangeText} onPatch={onPatch} onOpenMedia={() => onOpenMedia({ kind: 'block', blockId: block.blockId })} />
      )}

      {['heading', 'paragraph', 'rich_text', 'quote', 'badge', 'button'].includes(block.type) ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Opmaak</div>
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Lettertype" value={typography.fontFamily ?? 'site-default'} onChange={(event) => onPatch({ typography: { ...typography, fontFamily: event.target.value } })} className="h-9 min-w-32 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700">
              <option value="site-default">Site standaard</option><option value="inter">Inter</option><option value="manrope">Manrope</option><option value="dm-sans">DM Sans</option><option value="source-serif">Source Serif</option><option value="space-grotesk">Space Grotesk</option>
            </select>
            <label className="flex h-9 items-center rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-700"><input aria-label="Lettergrootte" type="number" min={10} max={180} value={typography.fontSize ?? ''} placeholder="auto" onChange={(event) => onPatch({ typography: { ...typography, fontSize: event.target.value ? Number(event.target.value) : undefined } })} className="w-12 bg-transparent text-center outline-none" /><span className="text-[10px] text-slate-400">px</span></label>
            <button type="button" aria-label="Vet" aria-pressed={(typography.fontWeight ?? 400) >= 600} onClick={() => onPatch({ typography: { ...typography, fontWeight: (typography.fontWeight ?? 400) >= 600 ? 400 : 700 } })} className={cn('h-9 rounded-lg px-3 text-sm font-bold', (typography.fontWeight ?? 400) >= 600 ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-700')}>B</button>
            <div className="flex h-9 items-center rounded-lg border border-slate-200 p-0.5">
              {([['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]] as const).map(([align, Icon]) => <button key={align} type="button" aria-label={`Uitlijnen ${align}`} aria-pressed={(typography.textAlign ?? 'left') === align} onClick={() => onPatch({ typography: { ...typography, textAlign: align } })} className={cn('rounded-md p-1.5', (typography.textAlign ?? 'left') === align ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-50')}><Icon className="h-4 w-4" /></button>)}
            </div>
          </div>
        </div>
      ) : null}

      {ICON_CAPABLE_BLOCKS.has(block.type) ? <ElementIconInspector block={block} onPatch={onPatch} /> : null}

      <div className="border-t border-slate-100 pt-4">
        <Choice
          label="Effect bij tonen"
          value={block.motion ?? 'none'}
          options={[
            ['none', 'Geen'],
            ['fade', 'Vervagen'],
            ['slide-up', 'Invliegen omhoog'],
            ['slide-down', 'Invliegen omlaag'],
            ['slide-left', 'Invliegen links'],
            ['slide-right', 'Invliegen rechts'],
            ['zoom', 'Inzoomen'],
            ['blur', 'Onscherp naar scherp'],
            ['rotate', 'Draai in'],
            ['flip', 'Flip in'],
            ['reveal', 'Reveal'],
            ['assemble', 'Blokken vormen'],
          ]}
          onChange={(value) => onPatch({ motion: value as EntranceEffect })}
        />
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className="text-xs font-semibold text-slate-600">
            Vertraging (ms)
            <input type="number" min={0} max={2000} step={50} value={block.motionDelay ?? 0} onChange={(event) => onPatch({ motionDelay: Math.max(0, Math.min(2000, Number(event.target.value) || 0)) })} className={fieldClass} />
          </label>
          <label className="text-xs font-semibold text-slate-600">
            Duur (ms)
            <input type="number" min={200} max={2000} step={50} value={block.motionDuration ?? 620} onChange={(event) => onPatch({ motionDuration: Math.max(200, Math.min(2000, Number(event.target.value) || 620)) })} className={fieldClass} />
          </label>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">Een rustige entree-animatie op de gepubliceerde pagina. Respecteert automatisch de voorkeur voor minder beweging.</p>
      </div>

      <label className="block text-sm font-semibold text-slate-800">
        Developer-klassen
        <input
          value={(block.customClasses ?? []).join(' ')}
          onChange={(event) => onPatch({ customClasses: event.target.value.split(/\s+/).map((item) => item.trim()).filter(Boolean).slice(0, 8) })}
          className={fieldClass}
          placeholder="bijv. hero-title"
          aria-describedby="sites-developer-class-help"
        />
        <span id="sites-developer-class-help" className="mt-1 block text-xs font-normal leading-relaxed text-slate-500">Veilige naamtokens voor Page-CSS, bijvoorbeeld <code>hero-title</code>.</span>
      </label>

      <details className="group border-t border-slate-100 pt-4">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-bold text-slate-900">
          <span>Positie & geavanceerd</span>
          <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">{inherited ? 'Overgenomen' : 'Aangepast'}</span>
        </summary>
        <div className="mt-4 space-y-4">
        <button type="button" onClick={() => patchPlacement({ offsetX: 0, offsetY: 0 })} className="text-xs font-semibold text-teal-700 hover:text-teal-900">Reset positie naar nul</button>
        {viewport !== 'desktop' ? <button type="button" onClick={resetPlacement} disabled={inherited} className="ml-3 text-xs font-semibold text-teal-700 disabled:text-slate-400">Reset naar desktop</button> : null}
        <div className="grid grid-cols-2 gap-2">
          {([
            ['gridStart', 'Kolom', 1, GRID_COLUMNS[viewport]], ['gridSpan', 'Breedte (kolommen)', 1, GRID_COLUMNS[viewport]],
            ['width', 'Breedte (px)', 40, 2000], ['height', 'Hoogte (px)', 40, 1200],
            ['offsetX', 'X-offset', -1200, 1200], ['offsetY', 'Y-offset', -1200, 1200],
          ] as const).map(([key, label, min, max]) => (
            <label key={key} className="text-xs font-semibold text-slate-600">
              {label}
              <input
                type="number" min={min} max={max}
                value={placement[key] ?? (key === 'gridSpan' ? GRID_COLUMNS[viewport] : key === 'height' ? 160 : key === 'width' ? '' : 0)}
                onChange={(event) => patchPlacement({ [key]: Number(event.target.value) })}
                className={fieldClass}
              />
            </label>
          ))}
        </div>
        <Choice
          label="Laag"
          value={placement.layer ?? 'normal'}
          options={[['background', 'Achtergrond'], ['normal', 'Normaal'], ['foreground', 'Voorgrond']]}
          onChange={(value) => patchPlacement({ layer: value as NonNullable<typeof placement['layer']> })}
        />
        {['heading', 'paragraph', 'rich_text', 'quote', 'badge', 'button'].includes(block.type) ? <>
        <div className="flex items-center justify-between">
          <label htmlFor="sites-font-size" className="text-xs font-semibold text-slate-600">Grootte (px)</label>
          <span className="text-[11px] font-medium text-slate-500">{typography.fontSize != null ? 'Aangepast' : 'Overgenomen van site'}</span>
        </div>
        <input id="sites-font-size" type="number" min={10} max={180} value={typography.fontSize ?? ''} onChange={(event) => onPatch({ typography: { ...typography, fontSize: event.target.value ? Number(event.target.value) : undefined } })} placeholder="Site standaard" className={fieldClass} />
        <p className="text-xs text-slate-500">Effectief: {typography.fontSize != null ? `${typography.fontSize}px` : block.type === 'heading' ? 'site heading-schaal' : 'site body-schaal'}</p>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-semibold text-slate-600">Lettertype
            <select value={typography.fontFamily ?? 'site-default'} onChange={(event) => onPatch({ typography: { ...typography, fontFamily: event.target.value } })} className={fieldClass}>
              <option value="site-default">Site standaard</option><option value="inter">Inter</option><option value="manrope">Manrope</option><option value="dm-sans">DM Sans</option><option value="source-serif">Source Serif</option><option value="space-grotesk">Space Grotesk</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-600">Gewicht
            <select value={typography.fontWeight ?? ''} onChange={(event) => onPatch({ typography: { ...typography, fontWeight: event.target.value ? Number(event.target.value) as 300 | 400 | 500 | 600 | 700 | 800 | 900 : undefined } })} className={fieldClass}>
              <option value="">Site standaard</option>{[300, 400, 500, 600, 700, 800, 900].map((weight) => <option key={weight} value={weight}>{weight}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-semibold text-slate-600">Regelhoogte
            <input type="number" min={0.8} max={2} step={0.05} value={typography.lineHeight ?? ''} onChange={(event) => onPatch({ typography: { ...typography, lineHeight: event.target.value ? Number(event.target.value) : undefined } })} placeholder="Site standaard" className={fieldClass} />
          </label>
          <label className="text-xs font-semibold text-slate-600">Letterafstand
            <input type="number" min={-10} max={20} step={0.5} value={typography.letterSpacing ?? ''} onChange={(event) => onPatch({ typography: { ...typography, letterSpacing: event.target.value ? Number(event.target.value) : undefined } })} placeholder="Site standaard" className={fieldClass} />
          </label>
        </div>
        <label className="block text-xs font-semibold text-slate-600">Tekstkleur
          <select value={typography.color ?? ''} onChange={(event) => onPatch({ typography: { ...typography, color: event.target.value ? event.target.value as ColorRole : undefined, customColor: null } })} className={fieldClass}>
            <option value="">Site standaard</option>
            {COLOR_ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <HexColorField label="Exacte tekstkleur" value={typography.customColor} onChange={(value) => onPatch({ typography: { ...typography, customColor: value } })} />
        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span>{typographyInherited ? 'Typografie volgt de site' : 'Typografie bevat lokale overrides'}</span>
          <button type="button" onClick={resetTypography} disabled={typographyInherited} className="font-semibold text-teal-700 disabled:text-slate-400">Reset typografie</button>
        </div>
        </> : null}
        </div>
      </details>

      <div className="flex gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={onDuplicate}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          <Copy className="h-4 w-4" />
          Dupliceren
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
          Verwijderen
        </button>
      </div>
    </div>
  );
}

const ICON_CAPABLE_BLOCKS = new Set<ContentBlock['type']>([
  'heading', 'paragraph', 'rich_text', 'badge', 'quote', 'faq',
  'image', 'shape', 'gallery', 'contact', 'form', 'video', 'custom_html',
]);

function ElementIconInspector({ block, onPatch }: { block: ContentBlock; onPatch: (patch: Partial<ContentBlock>) => void }) {
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4">
      <IconPicker value={block.icon ?? 'none'} onChange={(icon) => onPatch({ icon })} label="Icoon voor dit element" />
      {block.icon && block.icon !== 'none' ? (
        <Choice
          label="Icoonpositie"
          value={block.iconPosition ?? 'top'}
          options={[['top', 'Boven'], ['left', 'Links'], ['right', 'Rechts']]}
          onChange={(value) => onPatch({ iconPosition: value as ElementIconPosition })}
        />
      ) : null}
    </div>
  );
}

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

function HexColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string | null;
  onChange: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const validValue = HEX_COLOR_PATTERN.test(draft) ? draft : '#ffffff';

  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
        <input
          type="color"
          value={validValue}
          onChange={(event) => {
            setDraft(event.target.value);
            onChange(event.target.value);
          }}
          aria-label={`${label} kiezen`}
          className="h-9 w-10 cursor-pointer rounded-lg border border-slate-300 bg-white p-1"
        />
        <input
          value={draft}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            if (next === '') onChange(null);
            else if (HEX_COLOR_PATTERN.test(next)) onChange(next);
          }}
          onBlur={() => setDraft(value ?? '')}
          placeholder="#RRGGBB"
          inputMode="text"
          pattern="^#[0-9a-fA-F]{6}$"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-2 font-mono text-xs outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
        />
        {value ? <button type="button" onClick={() => { setDraft(''); onChange(null); }} className="px-1 text-xs font-semibold text-slate-500 hover:text-red-600">Wis</button> : null}
      </div>
      <span className="mt-1 block text-[11px] font-normal text-slate-500">Laat leeg om de preset te gebruiken.</span>
    </label>
  );
}

function developerCssAffectsBlock(page: SitePage, block: ContentBlock): boolean {
  if (!page.developerCss || !block.customClasses?.length) return false;
  return block.customClasses.some((className) => new RegExp(`\\.${className.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}(?:\\b|:)`).test(page.developerCss ?? ''));
}

function DeveloperCssNotice({ onOpenDeveloper }: { onOpenDeveloper?: () => void }) {
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-3 text-xs text-violet-950" data-testid="developer-css-active">
      <p className="font-bold">Developer CSS actief</p>
      <p className="mt-1 leading-relaxed text-violet-800">Een CSS-regel kan de visuele waarde van dit element overschrijven.</p>
      {onOpenDeveloper ? <button type="button" onClick={onOpenDeveloper} className="mt-2 font-bold text-violet-700 underline underline-offset-2">Open Developer Mode</button> : null}
    </div>
  );
}

function TypedBlockContent({
  block,
  definition,
  onChangeText,
  onPatch,
  onOpenMedia,
}: {
  block: ContentBlock;
  definition: ReturnType<typeof blockDefinition>;
  onChangeText: (text: string) => void;
  onPatch: (patch: Partial<ContentBlock>) => void;
  onOpenMedia: () => void;
}) {
  const textTypes = new Set(['heading', 'paragraph', 'rich_text', 'quote', 'badge']);
  if (textTypes.has(block.type)) {
    return (
      <div className="space-y-4">
        <label htmlFor="sites-block-text" className="block text-sm font-semibold text-slate-800">Inhoud</label>
        <textarea id="sites-block-text" rows={definition.multiline ? 7 : 3} value={block.text} onChange={(event) => onChangeText(event.target.value)} placeholder={definition.placeholder} className={`${fieldClass} resize-y`} />
        <p className="mt-1.5 text-xs text-slate-500">Direct bewerken op het canvas is de snelste manier.</p>
        {block.type === 'badge' ? <Choice label="Vorm" value={block.variant ?? 'pill'} options={[['plain', 'Eenvoudig'], ['pill', 'Pill'], ['accent', 'Accent']]} onChange={(value) => onPatch({ variant: value })} /> : null}
      </div>
    );
  }
  if (block.type === 'divider') {
    return (
      <div className="space-y-4">
        <Choice label="Stijl" value={block.variant ?? 'solid'} options={[['solid', 'Eenvoudig'], ['dashed', 'Gestreept'], ['dotted', 'Gestippeld']]} onChange={(value) => onPatch({ variant: value })} />
        <Choice label="Kleur" value={block.typography?.color ?? 'muted'} options={COLOR_ROLE_OPTIONS} onChange={(value) => onPatch({ typography: { ...block.typography, color: value as ColorRole, customColor: null } })} />
        <HexColorField label="Exacte lijnkleur" value={block.typography?.customColor} onChange={(value) => onPatch({ typography: { ...block.typography, customColor: value } })} />
      </div>
    );
  }
  if (block.type === 'spacer') {
    return <Choice label="Ruimte" value={block.size ?? 'md'} options={[['sm', 'Klein'], ['md', 'Normaal'], ['lg', 'Groot']]} onChange={(value) => onPatch({ size: value as ContentBlock['size'] })} />;
  }
  if (block.type === 'image') {
    return (
      <div className="space-y-4">
        <label className="block text-sm font-semibold text-slate-800">Afbeelding<button type="button" onClick={onOpenMedia} className="mt-2 w-full rounded-xl border border-dashed border-teal-300 bg-teal-50 p-4 text-center text-sm font-semibold text-teal-800 hover:bg-teal-100">{block.assetId ? 'Afbeelding wijzigen' : 'Afbeelding uploaden'}<span className="mt-1 block text-xs font-normal text-teal-700">Open de mediabibliotheek</span></button></label>
        <label className="block text-sm font-semibold text-slate-800">Alt-tekst<input value={block.alt ?? ''} onChange={(event) => onPatch({ alt: event.target.value })} className={fieldClass} placeholder="Beschrijf de afbeelding" /></label>
        <label className="block text-sm font-semibold text-slate-800">Bijschrift<input value={block.caption ?? ''} onChange={(event) => onPatch({ caption: event.target.value })} className={fieldClass} placeholder="Optioneel" /></label>
      </div>
    );
  }
  if (block.type === 'shape') {
    return (
      <div className="space-y-4">
        <label className="block text-sm font-semibold text-slate-800">
          Tekst in de vorm
          <textarea value={block.text} onChange={(event) => onChangeText(event.target.value)} rows={4} className={`${fieldClass} resize-y`} placeholder="Optionele tekst boven op de vorm" />
          <span className="mt-1.5 block text-xs font-normal text-slate-500">Je kunt deze tekst ook rechtstreeks in de vorm aanklikken.</span>
        </label>
        <VisualSurfaceInspector block={block} onPatch={onPatch} />
      </div>
    );
  }
  if (block.type === 'video') {
    return <div className="space-y-4"><label className="block text-sm font-semibold text-slate-800">Videoadres<input value={block.url ?? block.videoId ?? ''} onChange={(event) => onPatch({ url: event.target.value })} className={fieldClass} placeholder="YouTube, Vimeo of asset" /></label><label className="block text-sm font-semibold text-slate-800">Bijschrift<input value={block.caption ?? ''} onChange={(event) => onPatch({ caption: event.target.value })} className={fieldClass} /></label></div>;
  }
  if (block.type === 'contact') {
    return <div className="space-y-4"><TextInput label="Organisatie" value={block.organizationName ?? ''} onChange={(organizationName) => onPatch({ organizationName })} /><TextInput label="Adres" value={block.address ?? ''} onChange={(address) => onPatch({ address })} /><TextInput label="E-mail" value={block.email ?? ''} onChange={(email) => onPatch({ email })} /><TextInput label="Telefoon" value={block.phone ?? ''} onChange={(phone) => onPatch({ phone })} /></div>;
  }
  if (block.type === 'form') {
    const fields = block.fields ?? [];
    const patchField = (index: number, patch: Partial<NonNullable<ContentBlock['fields']>[number]>) => onPatch({ fields: fields.map((field, fieldIndex) => fieldIndex === index ? { ...field, ...patch } : field) });
    return (
      <div className="space-y-4">
        <TextInput label="Formuliertitel" value={block.title ?? ''} onChange={(title) => onPatch({ title })} />
        <label className="block text-sm font-semibold text-slate-800">Introductie<textarea value={block.description ?? ''} onChange={(event) => onPatch({ description: event.target.value })} rows={3} className={`${fieldClass} resize-y`} /></label>
        <TextInput label="Knoptekst" value={block.submitLabel ?? ''} onChange={(submitLabel) => onPatch({ submitLabel })} />
        <label className="block text-sm font-semibold text-slate-800">Succesmelding<textarea value={block.successMessage ?? ''} onChange={(event) => onPatch({ successMessage: event.target.value })} rows={2} className={`${fieldClass} resize-y`} /></label>
        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-bold text-slate-900">Velden</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">De inzending gaat veilig via Zoea naar de serverinstelling voor formulieren.</p>
        </div>
        {fields.map((field, index) => (
          <div key={field.fieldId || index} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wide text-slate-500">Veld {index + 1}</span><button type="button" onClick={() => onPatch({ fields: fields.filter((_, fieldIndex) => fieldIndex !== index) })} className="text-xs font-semibold text-red-600 hover:text-red-700">Verwijderen</button></div>
            <TextInput label="Label" value={field.label} onChange={(label) => patchField(index, { label })} />
            <Choice label="Type" value={field.type} options={[['text', 'Tekst'], ['email', 'E-mail'], ['textarea', 'Meerdere regels'], ['select', 'Keuzelijst'], ['checkbox', 'Toestemming']]} onChange={(type) => patchField(index, { type: type as NonNullable<ContentBlock['fields']>[number]['type'] })} />
            {field.type === 'select' ? <TextInput label="Opties (komma's)" value={(field.options ?? []).join(', ')} onChange={(value) => patchField(index, { options: value.split(',').map((option) => option.trim()).filter(Boolean) })} /> : null}
            {field.type !== 'checkbox' ? <TextInput label="Placeholder" value={field.placeholder ?? ''} onChange={(placeholder) => patchField(index, { placeholder })} /> : null}
            <label className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={field.required ?? false} onChange={(event) => patchField(index, { required: event.target.checked })} /> Verplicht veld</label>
          </div>
        ))}
        {fields.length < 12 ? <button type="button" onClick={() => onPatch({ fields: [...fields, { fieldId: `veld-${fields.length + 1}`, label: 'Nieuw veld', type: 'text', required: false, placeholder: '' }] })} className="w-full rounded-xl border border-dashed border-teal-300 px-3 py-2.5 text-sm font-semibold text-teal-700 hover:bg-teal-50">+ Veld toevoegen</button> : null}
      </div>
    );
  }
  if (block.type === 'feature_list') {
    return (
      <div className="space-y-4">
        <label className="block text-sm font-semibold text-slate-800">Punten
          <textarea value={(block.features ?? []).map((item) => item.text).join('\n')} onChange={(event) => onPatch({ features: event.target.value.split('\n').map((text) => text.trim()).filter(Boolean).map((text) => ({ text })) })} rows={7} className={`${fieldClass} resize-y`} placeholder="Eén punt per regel" />
        </label>
        <Choice label="Icoon" value={block.icon ?? 'check'} options={[['check', 'Vinkje'], ['dot', 'Punt'], ['arrow', 'Pijl']]} onChange={(value) => onPatch({ icon: value as ContentBlock['icon'] })} />
        <p className="text-xs leading-relaxed text-slate-500">Zet elk punt op een nieuwe regel. De lijst blijft automatisch netjes uitgelijnd.</p>
      </div>
    );
  }
  if (block.type === 'faq') {
    const questions = block.questions ?? [];
    return (
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-slate-500">Bewerk vraag en antwoord per item. Op de website worden ze als uitklapbare vragen getoond.</p>
        {questions.map((item, index) => (
          <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wide text-slate-500">Vraag {index + 1}</span><button type="button" onClick={() => onPatch({ questions: questions.filter((_, itemIndex) => itemIndex !== index) })} className="text-xs font-semibold text-red-600 hover:text-red-700">Verwijderen</button></div>
            <input value={item.question} onChange={(event) => onPatch({ questions: questions.map((current, itemIndex) => itemIndex === index ? { ...current, question: event.target.value } : current) })} className={fieldClass} placeholder="Vraag" />
            <textarea value={item.answer} onChange={(event) => onPatch({ questions: questions.map((current, itemIndex) => itemIndex === index ? { ...current, answer: event.target.value } : current) })} rows={3} className={`${fieldClass} mt-2 resize-y`} placeholder="Antwoord" />
          </div>
        ))}
        <button type="button" onClick={() => onPatch({ questions: [...questions, { question: 'Nieuwe vraag', answer: 'Nieuw antwoord' }] })} className="w-full rounded-xl border border-dashed border-teal-300 px-3 py-2.5 text-sm font-semibold text-teal-700 hover:bg-teal-50">+ Vraag toevoegen</button>
      </div>
    );
  }
  if (block.type === 'gallery') {
    return (
      <div className="space-y-4">
        <Choice label="Indeling" value={block.variant ?? 'grid'} options={[['grid', 'Raster'], ['feature', 'Uitgelicht']]} onChange={(value) => onPatch({ variant: value })} />
        <button type="button" onClick={onOpenMedia} className="w-full rounded-xl border border-dashed border-teal-300 bg-teal-50 px-3 py-2.5 text-left text-sm font-semibold text-teal-800 hover:bg-teal-100">Afbeeldingen uploaden of kiezen →</button>
        <label className="block text-sm font-semibold text-slate-800">Media-id&apos;s
          <textarea value={(block.assetIds ?? []).join('\n')} onChange={(event) => onPatch({ assetIds: event.target.value.split('\n').map((value) => value.trim()).filter(Boolean) })} rows={5} className={`${fieldClass} resize-y`} placeholder="Eén media-id per regel" />
        </label>
        <p className="text-xs leading-relaxed text-slate-500">Kies afbeeldingen via Media, of plak hier bestaande media-id&apos;s.</p>
      </div>
    );
  }
  return <p className="rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">De instellingen voor dit onderdeel staan onder Ontwerp, Positie of de specifieke editor.</p>;
}

function TextInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="block text-sm font-semibold text-slate-800">{label}<input value={value} onChange={(event) => onChange(event.target.value)} className={fieldClass} /></label>;
}

function VisualSurfaceInspector({ block, onPatch }: { block: ContentBlock; onPatch: (patch: Partial<ContentBlock>) => void }) {
  const surface = block.shape ?? {};
  const patchSurface = (patch: Partial<NonNullable<ContentBlock['shape']>>) => onPatch({ shape: { ...surface, ...patch } });
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-slate-900">Visuele vorm</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">Maak een decoratief vlak zonder code. De vorm blijft onderdeel van je normale pagina-indeling.</p>
      </div>
      <Choice label="Vorm" value={surface.shape ?? 'rounded'} options={[['rectangle', 'Rechthoek'], ['rounded', 'Rond vlak'], ['ellipse', 'Ovaal'], ['blob', 'Organisch'], ['line', 'Lijn']]} onChange={(value) => patchSurface({ shape: value as NonNullable<ContentBlock['shape']>['shape'] })} />
      <Choice label="Vulling" value={surface.fillMode ?? 'theme'} options={[['theme', 'Themakleur'], ['custom', 'Eigen kleur'], ['gradient', 'Verloop']]} onChange={(value) => patchSurface({ fillMode: value as NonNullable<ContentBlock['shape']>['fillMode'] })} />
      {surface.fillMode === 'gradient' ? <>
        <Choice label="Startkleur" value={surface.gradientFrom ?? 'primary'} options={COLOR_ROLE_OPTIONS} onChange={(value) => patchSurface({ gradientFrom: value as ColorRole, gradientFromCustom: null })} />
        <HexColorField label="Exacte startkleur" value={surface.gradientFromCustom} onChange={(value) => patchSurface({ gradientFromCustom: value })} />
        <Choice label="Eindkleur" value={surface.gradientTo ?? 'accent'} options={COLOR_ROLE_OPTIONS} onChange={(value) => patchSurface({ gradientTo: value as ColorRole, gradientToCustom: null })} />
        <HexColorField label="Exacte eindkleur" value={surface.gradientToCustom} onChange={(value) => patchSurface({ gradientToCustom: value })} />
        <Choice label="Richting" value={surface.gradientDirection ?? 'to-bottom-right'} options={[['to-right', 'Naar rechts'], ['to-bottom', 'Naar beneden'], ['to-bottom-right', 'Diagonaal'], ['radial', 'Radiaal']]} onChange={(value) => patchSurface({ gradientDirection: value as NonNullable<ContentBlock['shape']>['gradientDirection'] })} />
      </> : null}
      {surface.fillMode === 'theme' ? <Choice label="Themakleur" value={surface.fillColor ?? 'primary'} options={COLOR_ROLE_OPTIONS} onChange={(value) => patchSurface({ fillColor: value as ColorRole, customColor: null })} /> : null}
      {surface.fillMode === 'custom' ? <HexColorField label="Eigen kleur" value={surface.customColor} onChange={(value) => patchSurface({ customColor: value })} /> : null}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs font-semibold text-slate-600">Straal (px)<input type="number" min={0} max={96} value={surface.radius ?? 28} onChange={(event) => patchSurface({ radius: Number(event.target.value) })} className={fieldClass} /></label>
        <label className="text-xs font-semibold text-slate-600">Draaiing (°)<input type="number" min={-180} max={180} value={surface.rotation ?? 0} onChange={(event) => patchSurface({ rotation: Number(event.target.value) })} className={fieldClass} /></label>
      </div>
      <label className="block text-xs font-semibold text-slate-600">Dekking <span className="font-normal text-slate-400">{surface.opacity ?? 100}%</span><input type="range" min={0} max={100} value={surface.opacity ?? 100} onChange={(event) => patchSurface({ opacity: Number(event.target.value) })} className="mt-2 w-full" /></label>
      <Choice label="Schaduw" value={surface.shadow ?? 'none'} options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['medium', 'Middel'], ['strong', 'Sterk']]} onChange={(value) => patchSurface({ shadow: value as NonNullable<ContentBlock['shape']>['shadow'] })} />
      <Choice label="Rand" value={surface.borderStyle ?? 'none'} options={[['none', 'Geen'], ['solid', 'Eenvoudig'], ['dashed', 'Gestreept']]} onChange={(value) => patchSurface({ borderStyle: value as NonNullable<ContentBlock['shape']>['borderStyle'] })} />
      {surface.borderStyle && surface.borderStyle !== 'none' ? <><div className="grid grid-cols-2 gap-2"><label className="text-xs font-semibold text-slate-600">Dikte (px)<input type="number" min={0} max={12} value={surface.borderWidth ?? 1} onChange={(event) => patchSurface({ borderWidth: Number(event.target.value) })} className={fieldClass} /></label><label className="text-xs font-semibold text-slate-600">Randkleur<select value={surface.borderColor ?? 'light'} onChange={(event) => patchSurface({ borderColor: event.target.value as ColorRole, borderCustomColor: null })} className={fieldClass}>{COLOR_ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label></div><HexColorField label="Exacte randkleur" value={surface.borderCustomColor} onChange={(value) => patchSurface({ borderCustomColor: value })} /></> : null}
    </div>
  );
}

function PageInspector({
  page,
  pages,
  onUpdatePage,
  site,
  onModeChange,
}: {
  page: SitePage;
  pages: SitePage[];
  onUpdatePage: (
    pageId: string,
    patch: Partial<Pick<SitePage, 'title' | 'slug' | 'description'>>,
  ) => void;
  site: SiteChrome | null;
  onModeChange: (mode: 'INSPECTOR_PAGE' | 'SITE_DESIGN') => void;
}) {
  const isHomepage = page.slug === '';
  const [slugDraft, setSlugDraft] = useState(page.slug);

  // Het adres wordt pas doorgevoerd als het geldig én vrij is; tot die tijd
  // blijft de invoer staan met uitleg. Zo kan een halfgetypt adres nooit
  // stilzwijgend een tweede pagina op hetzelfde pad opleveren.
  useEffect(() => {
    setSlugDraft(page.slug);
  }, [page.pageId, page.slug]);

  const slugError = useMemo(() => {
    if (isHomepage) return null;
    if (slugDraft === page.slug) return null;
    const check = normalizeSlug(slugDraft);
    if (!check.ok) return check.error ?? 'Ongeldig adres.';
    if (check.value === '') return 'Vul een pagina-adres in.';
    if (pages.some((item) => item.slug === check.value && item.pageId !== page.pageId)) {
      return 'Er is al een pagina met dit adres.';
    }
    return null;
  }, [slugDraft, page.slug, page.pageId, pages, isHomepage]);

  const commitSlug = () => {
    if (isHomepage || slugError) return;
    const check = normalizeSlug(slugDraft);
    if (check.ok && check.value !== '' && check.value !== page.slug) {
      onUpdatePage(page.pageId, { slug: check.value });
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label htmlFor="sites-inspector-title" className="block text-sm font-semibold text-slate-800">
          Titel
        </label>
        <input
          id="sites-inspector-title"
          value={page.title}
          onChange={(event) => onUpdatePage(page.pageId, { title: event.target.value })}
          placeholder="Naamloze pagina"
          className={fieldClass}
        />
      </div>

      <div>
        <label htmlFor="sites-inspector-slug" className="block text-sm font-semibold text-slate-800">
          Adres
        </label>
        {isHomepage ? (
          <p className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-500">
            Dit is de homepage en staat altijd op{' '}
            <span className="font-semibold text-slate-700">/</span>
          </p>
        ) : (
          <>
            <div className="mt-1.5 flex items-center rounded-xl border border-slate-300 px-3.5 transition focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/20">
              <span className="select-none text-sm text-slate-400">/</span>
              <input
                id="sites-inspector-slug"
                value={slugDraft}
                onChange={(event) => setSlugDraft(event.target.value)}
                onBlur={commitSlug}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                }}
                aria-invalid={Boolean(slugError)}
                className="w-full bg-transparent py-2.5 pl-0.5 text-sm outline-none"
              />
            </div>
            {slugError ? (
              <p role="alert" className="mt-1.5 text-xs font-medium text-red-600">
                {slugError}
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-slate-500">
                Zichtbaar op{' '}
                <span className="font-medium text-slate-700">{displayPath(page.slug)}</span>
              </p>
            )}
          </>
        )}
      </div>

      <div>
        <label
          htmlFor="sites-inspector-description"
          className="block text-sm font-semibold text-slate-800"
        >
          Korte omschrijving <span className="font-normal text-slate-400">(optioneel)</span>
        </label>
        <textarea
          id="sites-inspector-description"
          rows={3}
          value={page.description ?? ''}
          onChange={(event) =>
            onUpdatePage(page.pageId, { description: event.target.value || null })
          }
          placeholder="Wordt gebruikt in zoekresultaten."
          className={`${fieldClass} resize-none`}
        />
      </div>

      <p className="border-t border-slate-100 pt-4 text-xs text-slate-500">
        {page.blocks.length === 0
          ? 'Deze pagina heeft nog geen blokken.'
          : `${page.blocks.length} ${page.blocks.length === 1 ? 'blok' : 'blokken'} op deze pagina.`}
      </p>
      {site ? (
        <button
          type="button"
          onClick={() => onModeChange('SITE_DESIGN')}
          className="mt-2 flex w-full items-center justify-between rounded-xl border border-slate-200 px-3.5 py-3 text-sm font-semibold text-slate-800 transition hover:border-teal-300 hover:bg-teal-50"
        >
          Ontwerp van je website
          <span className="text-xs font-normal text-slate-500">Kleuren, typografie, vormen</span>
        </button>
      ) : null}
    </div>
  );
}

function DesignControls({ site, onUpdateTheme, onUpdateHeader }: { site: SiteChrome; onUpdateTheme: (patch: Partial<SiteChrome['theme']>) => void; onUpdateHeader: (patch: Partial<SiteChrome['header']>) => void }) {
  const colors = site.theme.colors;
  const paletteControls: Array<[Exclude<ColorRole, 'light'>, string]> = [
    ['primary', 'Hoofdkleur'],
    ['secondary', 'Secundair'],
    ['accent', 'Accent'],
    ['background', 'Achtergrond'],
    ['surface', 'Vlak'],
    ['text', 'Tekst'],
    ['muted', 'Gedempt'],
    ['dark', 'Donker'],
  ];
  return <div className="border-t border-slate-100 pt-5">
    <h3 className="text-sm font-bold text-slate-900">Ontwerp van je website</h3>
    <p className="mt-1 text-xs leading-relaxed text-slate-500">Kies exact iedere themakleur. Secties, kaarten, knoppen en tekst volgen deze keuzes tenzij je een onderdeel een eigen kleur geeft.</p>
    <div className="mt-4 grid grid-cols-2 gap-3">{paletteControls.map(([role, label]) => <label key={role} className="text-xs font-semibold text-slate-600">{label}<span className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 p-1.5"><input type="color" value={colors[role]} onChange={(event) => onUpdateTheme({ colors: { ...colors, [role]: event.target.value } })} className="h-7 w-8 cursor-pointer rounded border-0 bg-transparent p-0" /><span className="font-mono text-[10px] font-normal">{colors[role]}</span></span></label>)}</div>
    <label className="mt-4 block text-xs font-semibold text-slate-600">Header<select value={site.header.behavior} onChange={(event) => onUpdateHeader({ behavior: event.target.value as SiteChrome['header']['behavior'] })} className={fieldClass}><option value="static">Normaal</option><option value="sticky">Blijvend zichtbaar</option><option value="transparent">Transparant over hero</option></select></label>
  </div>;
}
